<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input lazy" @submit.prevent="storePlan">
            <v-card style="position: relative;">
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Add New Plan</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-overlay :model-value="!formReady" contained class="align-center justify-center">
                    <v-progress-circular indeterminate color="primary"/>
                </v-overlay>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field v-model="formData['name']" autofocus :rules="[vRules.required]" density="compact"
                                          label="Name"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['portal_id']" :items="portals"
                                      :rules="[vRules.required]" density="compact" item-title="name" item-value="id"
                                      label="Portal" @update:model-value="()=>{
                                          formData['skus']=null;fetchProductSkusList()
                                      }"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['skus']" :items="productSkus"
                                      :rules="[vRules.required]" chips density="compact" item-title="sku"
                                      item-value="id" label="Product Skus" multiple/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['price']" :rules="[vRules.required]" density="compact"
                                          label="Price" type="number"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['interval']" :items="intervalTypes" :rules="[vRules.required]"
                                      density="compact" label="Interval"/>
                        </v-col>

                        <!-- Go (personal portal) plans: premium flag + entitlement
                             feature values. Hidden for fleet plans. -->
                        <template v-if="portalType === 'personal'">
                            <v-col cols="12"><v-divider/></v-col>
                            <v-col cols="12" sm="4">
                                <v-switch v-model="formData['is_premium']" color="primary" density="compact"
                                          hide-details label="Premium plan"/>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field v-model.number="formData['tier_rank']" density="compact"
                                              label="Tier rank" type="number"/>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field v-model.number="formData['max_members']" density="compact"
                                              hint="Blank = not applicable" label="Max members" persistent-hint
                                              type="number"/>
                            </v-col>
                            <v-col cols="12"><div class="text-subtitle-2">Premium features</div></v-col>
                            <v-col v-for="f in featureCatalog" :key="f.key" cols="12" sm="6">
                                <v-switch v-if="f.type === 'bool'" v-model="formData.features[f.key]"
                                          color="primary" density="compact" hide-details
                                          :label="f.label || f.key"/>
                                <v-text-field v-else v-model.number="formData.features[f.key]" density="compact"
                                              :hint="f.type === 'unlimited' ? '-1 = unlimited' : ''" persistent-hint
                                              :label="f.label || f.key" type="number"/>
                            </v-col>
                        </template>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted, computed, watch} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const formReady = ref(false);
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
});
const formData = reactive({
    valid: false,
    name: null,
    skus: null,
    price: null,
    interval: null,
    portal_id: null,
    is_premium: false,
    tier_rank: 0,
    max_members: null,
    features: {},
});
const emits = defineEmits(['update:modelValue', 'addPlan']);
const dialogSwitch = ref(props.modelValue);
const productSkus = ref([]);
const portals = ref([]);
const featureCatalog = ref([]);
const errors = ref([]);
const intervalTypes = [
    {value: 0, title: 'Monthly'},
    {value: 1, title: 'Quarterly'},
    {value: 2, title: 'Yearly'},
];
const portalType = computed(() => {
    const p = portals.value.find((item) => item.id === formData['portal_id']);
    return p ? p.type : null;
})
onMounted(() => {
    Promise.all([fetchPortals(), fetchFeatureCatalog()]).finally(() => formReady.value = true);
});

const fetchFeatureCatalog = () => {
    return send('get', `admin/feature-catalog`)
        .then((response) => {
            featureCatalog.value = response.data.data;
        })
        .catch(() => {});
};

// Prefill the feature values from the catalog defaults when the plan is on the
// personal portal, so the admin edits real starting values.
const initFeatures = () => {
    const f = {};
    featureCatalog.value.forEach((c) => {
        if (c.type === 'bool') {
            f[c.key] = ['1', 'true', 'yes'].includes(String(c.default_value).toLowerCase());
        } else {
            f[c.key] = c.default_value === null ? null : Number(c.default_value);
        }
    });
    formData.features = f;
};
watch(portalType, (t) => {
    // Only personal-portal (Go) plans carry entitlement features; clear them for
    // fleet plans so no overrides are sent/created for them.
    if (t === 'personal') initFeatures();
    else formData.features = {};
});

const fetchPortals = () => {
    return send('get', `admin/portals`)
        .then((response) => {
            portals.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Portals could not be loaded', 'error')
        })
}
const fetchProductSkusList = () => {
    return send('get', `product-skus`, {list: true, type: portalType.value})
        .then((response) => {
            productSkus.value = response.data.data.product_skus;
        }).catch((error) => {
            snackbar.showSnackbar('Product skus list could not be loaded', 'error')
        })
}
const storePlan = () => {
    errors.value = [];
    send('post', `admin/subscription-plans`, formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('addPlan', response.data.data);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>
